import React, { useEffect, useRef, useState } from 'react';
import ConfirmationModal from '../business/store/ConfirmationModal.jsx';
import useAuth from '../hooks/AuthHook.jsx';
import { getLocationSuggestions, postLocumAddress } from '../Api.js';
import { STATES } from '../Constants';
import './CredentialsPromptModal.scss';

const SUGGESTION_TYPES = ['address', 'poi', 'place', 'locality', 'neighborhood', 'postcode'];

export const formatLocumAddress = (address) => {
  if (!address) {
    return '';
  }
  const place = [address.street, address.suburb, address.state].filter(Boolean).join(', ');
  return address.postcode ? `${place} ${address.postcode}`.trim() : place;
};

const mapboxContextText = (feature, typePrefix) => {
  if (feature.id && feature.id.includes(typePrefix)) {
    return feature.text || '';
  }
  const match = (feature.context || []).find(({ id }) => id && id.includes(typePrefix));
  return match ? match.text : '';
};

const addressFromSuggestion = (feature) => {
  const placeType = (feature.place_type || [])[0];
  const state = mapboxContextText(feature, 'region');
  const postcode = mapboxContextText(feature, 'postcode');
  const city = mapboxContextText(feature, 'place');
  const isStreet = placeType === 'address';
  const street = isStreet ? [feature.address, feature.text].filter(Boolean).join(' ') : '';
  const suburb = isStreet || placeType === 'poi' || placeType === 'postcode'
    ? (mapboxContextText(feature, 'locality') || mapboxContextText(feature, 'neighborhood') || mapboxContextText(feature, 'place') || '')
    : (feature.text || '');
  return { street, suburb, city, state, postcode };
};

const matchingState = (state) => (STATES.some((item) => item.text === state) ? state : '');

const LocumAddressModal = ({ close }) => {
  const { profile, setProfile } = useAuth();
  const address = profile.address || {};
  const [street, setStreet] = useState(address.street || '');
  const [suburb, setSuburb] = useState(address.suburb || '');
  const [city, setCity] = useState(address.city || '');
  const [state, setState] = useState(matchingState(address.state));
  const [postcode, setPostcode] = useState(/^\d{4}$/.test(`${address.postcode || ''}`) ? `${address.postcode}` : '');
  const [searchText, setSearchText] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [notice, setNotice] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorText, setErrorText] = useState('');
  const requestId = useRef(0);

  useEffect(() => {
    if (searchText.trim().length <= 2) {
      setSuggestions([]);
      setNotice('');
      return undefined;
    }
    const nextRequestId = requestId.current + 1;
    requestId.current = nextRequestId;
    const timeout = setTimeout(() => {
      getLocationSuggestions(searchText.trim())
        .then((response) => {
          if (requestId.current !== nextRequestId) {
            return;
          }
          const features = (response && response.features ? response.features : [])
            .filter((feature) => (feature.place_type || []).some((type) => SUGGESTION_TYPES.includes(type)))
            .filter((feature) => {
              const parsed = addressFromSuggestion(feature);
              return parsed.street || parsed.suburb || parsed.postcode;
            })
            .slice(0, 6);
          setSuggestions(features);
          setNotice(features.length === 0 ? 'No matching addresses. Type your suburb below.' : '');
        })
        .catch(() => {
          if (requestId.current === nextRequestId) {
            setSuggestions([]);
            setNotice('Couldn\'t search addresses. Type your suburb below.');
          }
        });
    }, 300);
    return () => {
      clearTimeout(timeout);
      requestId.current += 1;
    };
  }, [searchText]);

  const applySuggestion = (feature) => {
    const parsed = addressFromSuggestion(feature);
    setStreet(parsed.street);
    setSuburb(parsed.suburb);
    setCity(parsed.city || '');
    setState(matchingState(parsed.state));
    setPostcode(/^\d{4}$/.test(parsed.postcode) ? parsed.postcode : '');
    setSearchText('');
    setSuggestions([]);
    setNotice('');
    setErrorText('');
  };

  const confirm = () => {
    if (!suburb.trim()) {
      setErrorText('Enter a suburb.');
      return;
    }
    if (!state) {
      setErrorText('Select a state.');
      return;
    }
    if (postcode.trim() && !/^\d{4}$/.test(postcode.trim())) {
      setErrorText('Postcode must be 4 digits.');
      return;
    }
    setLoading(true);
    setErrorText('');
    postLocumAddress({ street, suburb, city, state, postcode })
      .then(({ address: nextAddress }) => {
        setProfile({ ...profile, address: nextAddress });
        setLoading(false);
        close();
      })
      .catch((err) => {
        setLoading(false);
        setErrorText(err && err.message ? err.message : 'Something went wrong saving your address. Please try again.');
      });
  };

  return (
    <ConfirmationModal
      loading={loading}
      close={close}
      additionalClassName={'address-prompt'}
      confirm={confirm}
      confirmText={'Save'}
      disabled={loading}
    >
      <h3 className={'confirmation-title'}>{'Your address'}</h3>
      <p className={'confirmation-text'}>
        {'Stores see your suburb and state when you request a shift. Your street stays on your account.'}
      </p>
      <label className={'address-field address-search'}>
        <span className={'address-label'}>{'Search address'}</span>
        <input
          className={'address-input'}
          type={'text'}
          value={searchText}
          placeholder={'Street or suburb'}
          autoComplete={'off'}
          onChange={(e) => {
            setSearchText(e.target.value);
            setErrorText('');
          }}
        />
      </label>
      {suggestions.length > 0 && (
        <ul className={'address-suggestions'}>
          {suggestions.map((suggestion) => (
            <li key={suggestion.id || suggestion.place_name}>
              <button type={'button'} onClick={() => applySuggestion(suggestion)}>
                {suggestion.place_name}
              </button>
            </li>
          ))}
        </ul>
      )}
      {notice && <p className={'address-notice'}>{notice}</p>}
      <p className={'address-or'}>{'Or enter your suburb'}</p>
      <label className={'address-field'}>
        <span className={'address-label'}>{'Street'}</span>
        <input className={'address-input'} type={'text'} value={street} placeholder={'Optional'} autoComplete={'off'} onChange={(e) => setStreet(e.target.value)} />
      </label>
      <label className={'address-field'}>
        <span className={'address-label'}>{'Suburb'}</span>
        <input className={'address-input'} type={'text'} value={suburb} autoComplete={'off'} onChange={(e) => setSuburb(e.target.value)} />
      </label>
      <div className={'address-split'}>
        <label className={'address-field'}>
          <span className={'address-label'}>{'State'}</span>
          <select className={'address-input'} value={state} onChange={(e) => setState(e.target.value)}>
            <option value={''}>{'Select state'}</option>
            {STATES.map((item) => (
              <option key={item.key} value={item.text}>{item.text}</option>
            ))}
          </select>
        </label>
        <label className={'address-field'}>
          <span className={'address-label'}>{'Postcode'}</span>
          <input className={'address-input'} type={'text'} inputMode={'numeric'} value={postcode} placeholder={'Optional'} autoComplete={'off'} onChange={(e) => setPostcode(e.target.value)} />
        </label>
      </div>
      {errorText && <p className={'error-text'}>{errorText}</p>}
    </ConfirmationModal>
  );
};

export default LocumAddressModal;
